// Shared range/refresh strip used by both the per-tenant Analytics tab and
// the global /admin/analytics page. Owns the preset selection (7d/30d/90d/
// Custom), the custom date inputs, the manual-refresh button, and the
// "last refreshed at" caption.

import { useMemo } from 'react'

import { useRefreshAnalytics } from '../hooks/useAnalytics.js'

const PRESETS: Array<{ key: '7' | '30' | '90'; label: string; days: number }> = [
  { key: '7', label: '7d', days: 7 },
  { key: '30', label: '30d', days: 30 },
  { key: '90', label: '90d', days: 90 },
]

export type RangePreset = '7' | '30' | '90' | 'custom'

interface Props {
  from: string
  to: string
  preset: RangePreset
  onChange: (from: string, to: string, preset: RangePreset) => void
  lastRefreshedAt: string | null | undefined
  loading?: boolean
}

function phToday(): string {
  return new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

function daysBeforeToday(days: number): string {
  return new Date(Date.now() + 8 * 60 * 60 * 1000 - (days - 1) * 86_400_000)
    .toISOString()
    .slice(0, 10)
}

function formatRefreshed(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'never'
  return d.toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function AnalyticsRangeBar({
  from,
  to,
  preset,
  onChange,
  lastRefreshedAt,
  loading,
}: Props) {
  const refresh = useRefreshAnalytics()

  const lastRefreshedLabel = useMemo(
    () => formatRefreshed(lastRefreshedAt),
    [lastRefreshedAt],
  )

  function selectPreset(key: '7' | '30' | '90') {
    const days = PRESETS.find((p) => p.key === key)!.days
    onChange(daysBeforeToday(days), phToday(), key)
  }

  function selectCustom() {
    // Keep the current from/to but switch into custom mode so the date
    // inputs become editable instead of fixed by the preset.
    onChange(from, to, 'custom')
  }

  return (
    <div className="admin-range-bar">
      <div className="admin-range-presets" role="tablist">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={preset === p.key}
            className={`admin-range-preset${
              preset === p.key ? ' admin-range-preset-active' : ''
            }`}
            onClick={() => selectPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          role="tab"
          aria-selected={preset === 'custom'}
          className={`admin-range-preset${
            preset === 'custom' ? ' admin-range-preset-active' : ''
          }`}
          onClick={selectCustom}
        >
          Custom
        </button>
      </div>

      {preset === 'custom' && (
        <div className="admin-range-dates">
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => onChange(e.target.value, to, 'custom')}
          />
          <span>→</span>
          <input
            type="date"
            value={to}
            min={from}
            max={phToday()}
            onChange={(e) => onChange(from, e.target.value, 'custom')}
          />
        </div>
      )}

      <div className="admin-range-refresh-meta">
        <span>
          Last refreshed: <strong>{lastRefreshedLabel}</strong>
        </span>
        <button
          type="button"
          className="admin-btn admin-btn-sm"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending || loading}
        >
          {refresh.isPending ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>
    </div>
  )
}

export function defaultRange(): { from: string; to: string; preset: RangePreset } {
  return { from: daysBeforeToday(30), to: phToday(), preset: '30' }
}

/** Inclusive day count for an analytics range. 2026-04-28..2026-05-27 → 30. */
export function rangeSpanDays(from: string, to: string): number {
  const fromMs = new Date(`${from}T00:00:00+08:00`).getTime()
  const toMs = new Date(`${to}T00:00:00+08:00`).getTime()
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs < fromMs) {
    return 0
  }
  return Math.round((toMs - fromMs) / 86_400_000) + 1
}
